import React, { useState, useEffect } from 'react';
import settingsService from '../services/settingsService';
import { useAuth } from '../context/AuthContext';
import appVersion from '../utils/appVersion';
import ProductImport from '../components/settings/ProductImport';

const toBoolean = (value, defaultValue = false) => {
  if (value === undefined || value === null || value === '') return defaultValue;
  return ['1', 'true', 'yes', 'y'].includes(String(value).trim().toLowerCase());
};

const SECTION_ITEMS = [
  { id: 'loyalty', label: 'Loyalty Program' },
  { id: 'security', label: 'Security' },
  { id: 'checkout', label: 'Checkout' },
  { id: 'purchaseOrders', label: 'Purchase Orders' },
  { id: 'discountReasons', label: 'Discount Reasons' },
  { id: 'imports', label: 'Product Import' },
];

const SettingsPage = () => {
  const [settings, setSettings] = useState({
    loyaltyPointsPerDollar: 1,
    loyaltyPointsToDollarRatio: 100,
    webMfaRequired: true,
    duplicateCashSalePreventionEnabled: true,
    duplicateCashSalePreventionWindowSeconds: 90,
    preventPurchaseOrderPriceDecrease: true,
    discountReasons: [],
  });
  const [activeSection, setActiveSection] = useState('loyalty');
  const [isLoading, setIsLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const { user } = useAuth();

  const canManageSettings = user?.permissions?.includes('settings:manage');
  const isEditableSection = ['loyalty', 'security', 'checkout', 'purchaseOrders', 'discountReasons'].includes(activeSection);

  useEffect(() => {
    let isMounted = true;
    const loadSettings = async () => {
      if (!canManageSettings) {
        if (isMounted) setIsLoading(false);
        return;
      }

      try {
        const data = await settingsService.getSettings();
        if (!isMounted) return;
        setSettings(prev => ({ ...prev, ...data }));
      } catch (error) {
        console.error('Failed to load settings', error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadSettings();
    return () => {
      isMounted = false;
    };
  }, [canManageSettings]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : Number(value),
    }));
  };

  const handleSave = async () => {
    try {
      const settingsToSave = activeSection === 'discountReasons'
        ? { discountReasons: settings.discountReasons }
        : settings;
      await settingsService.updateSettings(settingsToSave);
      setError('');
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error("Failed to save settings", error);
      setError(error.response?.data?.message || 'Settings could not be saved. Please correct the values and try again.');
    }
  };

  const createDiscountReasonId = () => (
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `discount-reason-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  );

  const addDiscountReason = () => {
    setSettings((prev) => ({
      ...prev,
      discountReasons: [
        ...(Array.isArray(prev.discountReasons) ? prev.discountReasons : []),
        { id: createDiscountReasonId(), name: '', isActive: true },
      ],
    }));
  };

  const updateDiscountReason = (id, updates) => {
    setSettings((prev) => ({
      ...prev,
      discountReasons: (prev.discountReasons || []).map((reason) => (
        reason.id === id ? { ...reason, ...updates } : reason
      )),
    }));
  };

  const moveDiscountReason = (index, direction) => {
    setSettings((prev) => {
      const reasons = [...(prev.discountReasons || [])];
      const destination = index + direction;
      if (destination < 0 || destination >= reasons.length) return prev;
      [reasons[index], reasons[destination]] = [reasons[destination], reasons[index]];
      return { ...prev, discountReasons: reasons };
    });
  };

  if (isLoading) return <div>Loading settings...</div>;

  if (!canManageSettings) {
    return <div className="p-6 text-red-500 font-semibold">Access Denied: You do not have permission to manage global settings.</div>;
  }

  const renderSectionContent = () => {
    if (activeSection === 'loyalty') {
      return (
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Loyalty Program</h3>
          <p className="text-sm text-gray-500 mt-1">Define how loyalty points are earned and redeemed across all stores.</p>
          <div className="mt-4 space-y-4 border-t pt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Points Earned Per Dollar Spent</label>
              <input
                type="number"
                name="loyaltyPointsPerDollar"
                value={settings.loyaltyPointsPerDollar}
                onChange={handleChange}
                className="mt-1 p-2 border rounded-md w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Points to Equal $1 for Redemption</label>
              <input
                type="number"
                name="loyaltyPointsToDollarRatio"
                value={settings.loyaltyPointsToDollarRatio}
                onChange={handleChange}
                className="mt-1 p-2 border rounded-md w-full"
              />
              <p className="text-xs text-gray-500 mt-1">e.g., a value of 100 means 100 points = $1.</p>
            </div>
          </div>
        </div>
      );
    }

    if (activeSection === 'security') {
      const webMfaEnabled = toBoolean(settings.webMfaRequired, true);
      return (
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Security</h3>
          <p className="text-sm text-gray-500 mt-1">Control authentication requirements for global web login.</p>
          <div className="mt-4 border-t pt-4">
            <label className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-sm font-medium text-gray-700">Require OTP for web login</p>
                <p className="text-xs text-gray-500">When enabled, email/password login requires a verification code.</p>
              </div>
              <div className="inline-flex items-center gap-3">
                <span className="text-sm text-slate-600">{webMfaEnabled ? 'On' : 'Off'}</span>
                <div className="relative inline-flex items-center">
                  <input
                    type="checkbox"
                    name="webMfaRequired"
                    checked={webMfaEnabled}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="h-6 w-11 rounded-full bg-slate-200 transition-colors peer-checked:bg-primary" />
                  <span className="absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
                </div>
              </div>
            </label>
          </div>
        </div>
      );
    }

    if (activeSection === 'purchaseOrders') {
      const preventPriceDecrease = toBoolean(settings.preventPurchaseOrderPriceDecrease, true);
      return (
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Purchase Orders</h3>
          <p className="text-sm text-gray-500 mt-1">Control how purchase order approval updates product pricing.</p>
          <div className="mt-4 border-t pt-4">
            <label className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-sm font-medium text-gray-700">Prevent PO price decreases</p>
                <p className="text-xs text-gray-500">
                  When enabled, approving a purchase order will not lower the destination store price if the PO line price is lower than the current store price.
                </p>
              </div>
              <div className="inline-flex items-center gap-3">
                <span className="text-sm text-slate-600">{preventPriceDecrease ? 'On' : 'Off'}</span>
                <div className="relative inline-flex items-center">
                  <input
                    type="checkbox"
                    name="preventPurchaseOrderPriceDecrease"
                    checked={preventPriceDecrease}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="h-6 w-11 rounded-full bg-slate-200 transition-colors peer-checked:bg-primary" />
                  <span className="absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
                </div>
              </div>
            </label>
          </div>
        </div>
      );
    }

    if (activeSection === 'checkout') {
      const duplicateCashSalePreventionEnabled = toBoolean(settings.duplicateCashSalePreventionEnabled, true);
      return (
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Checkout</h3>
          <p className="text-sm text-gray-500 mt-1">Control checkout safeguards for older or slow POS sessions.</p>
          <div className="mt-4 space-y-4 border-t pt-4">
            <label className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-sm font-medium text-gray-700">Prevent duplicate cash sales</p>
                <p className="text-xs text-gray-500">
                  Enabled by default. Blocks matching cash sales from the same store, till, and cashier within the configured window when the checkout request has no idempotency key.
                </p>
              </div>
              <div className="inline-flex items-center gap-3">
                <span className="text-sm text-slate-600">{duplicateCashSalePreventionEnabled ? 'On' : 'Off'}</span>
                <div className="relative inline-flex items-center">
                  <input
                    type="checkbox"
                    name="duplicateCashSalePreventionEnabled"
                    checked={duplicateCashSalePreventionEnabled}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="h-6 w-11 rounded-full bg-slate-200 transition-colors peer-checked:bg-primary" />
                  <span className="absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
                </div>
              </div>
            </label>
            <div>
              <label htmlFor="duplicateCashSalePreventionWindowSeconds" className="block text-sm font-medium text-gray-700">Duplicate match window seconds</label>
              <input
                id="duplicateCashSalePreventionWindowSeconds"
                type="number"
                name="duplicateCashSalePreventionWindowSeconds"
                min="5"
                max="600"
                step="1"
                value={settings.duplicateCashSalePreventionWindowSeconds}
                onChange={handleChange}
                className="mt-1 p-2 border rounded-md w-full"
              />
              <p className="text-xs text-gray-500 mt-1">Allowed range: 5 to 600 seconds.</p>
            </div>
          </div>
        </div>
      );
    }

    if (activeSection === 'discountReasons') {
      const reasons = Array.isArray(settings.discountReasons) ? settings.discountReasons : [];
      return (
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Discount Reasons</h3>
          <p className="text-sm text-gray-500 mt-1">Staff must choose an active reason before applying a manual discount at checkout.</p>
          <div className="mt-4 space-y-3 border-t pt-4">
            {reasons.length === 0 ? (
              <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">No discount reasons have been configured.</p>
            ) : reasons.map((reason, index) => (
              <div key={reason.id} className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center">
                <div className="flex gap-1">
                  <button
                    type="button"
                    aria-label={`Move ${reason.name || 'discount reason'} up`}
                    onClick={() => moveDiscountReason(index, -1)}
                    disabled={index === 0}
                    className="min-h-11 min-w-11 rounded border text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >↑</button>
                  <button
                    type="button"
                    aria-label={`Move ${reason.name || 'discount reason'} down`}
                    onClick={() => moveDiscountReason(index, 1)}
                    disabled={index === reasons.length - 1}
                    className="min-h-11 min-w-11 rounded border text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >↓</button>
                </div>
                <label className="flex-1">
                  <span className="sr-only">Discount reason {index + 1}</span>
                  <input
                    type="text"
                    value={reason.name}
                    onChange={(event) => updateDiscountReason(reason.id, { name: event.target.value })}
                    className="w-full rounded-md border p-2"
                    placeholder="Reason name"
                    maxLength="120"
                  />
                </label>
                <label className="flex min-h-11 items-center gap-2 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={reason.isActive !== false}
                    onChange={(event) => updateDiscountReason(reason.id, { isActive: event.target.checked })}
                  />
                  Active
                </label>
              </div>
            ))}
            <button type="button" onClick={addDiscountReason} className="min-h-11 rounded-md border border-primary px-4 text-sm font-semibold text-primary hover:bg-primary/5">
              Add discount reason
            </button>
          </div>
        </div>
      );
    }

    if (activeSection === 'imports') {
      return <ProductImport />;
    }

    return null;
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-800 mb-2">Global Settings</h1>
      <p className="text-sm text-gray-500 mb-4">BoltinPOS build {appVersion}</p>
      <div className="flex flex-col lg:flex-row lg:space-x-6">
        <aside className="lg:w-1/5 mb-6 lg:mb-0">
          <div className="bg-white p-4 rounded-lg shadow-md space-y-1">
            {SECTION_ITEMS.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => setActiveSection(section.id)}
                className={`block w-full text-left px-4 py-2 text-sm rounded-md ${
                  activeSection === section.id
                    ? 'bg-blue-100 text-blue-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>
        </aside>
        <main className="lg:w-4/5">
          <div className="bg-white p-6 rounded-lg shadow-md">
            {success && <p className="bg-green-100 text-green-700 p-3 rounded mb-4">{success}</p>}
            {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
            {renderSectionContent()}
            {isEditableSection && (
              <div className="flex justify-end mt-6 border-t pt-4">
                <button onClick={handleSave} className="min-h-11 bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark">Save Global Settings</button>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SettingsPage;
